import type { SubGamesConfig, PlayerTier, PlayerInfo, SubGamesEventMap } from '../types';

const DEFAULT_API_URL = 'https://api.sub.games';
const DEFAULT_APP_URL = 'https://sub.games';

const TIER_RANK: Record<PlayerTier, number> = {
  none: 0,
  free: 1,
  supporter: 2,
  founder: 3,
};

export class SubGamesSDK {
  private static instances = new Map<string, SubGamesSDK>();
  private config: Required<Pick<SubGamesConfig, 'gameKey' | 'apiUrl' | 'appUrl'>> & SubGamesConfig;
  private token: string | null = null;
  private cachedTier: PlayerTier | null = null;
  private listeners: Partial<Record<keyof SubGamesEventMap, Function[]>> = {};
  private promptTimer: ReturnType<typeof setTimeout> | null = null;
  private paused = false;
  private readyInterval: ReturnType<typeof setInterval> | null = null;
  private platformAuthenticated = false;

  constructor(config: SubGamesConfig) {
    this.config = {
      ...config,
      apiUrl: config.apiUrl || DEFAULT_API_URL,
      appUrl: config.appUrl || DEFAULT_APP_URL,
    };
  }

  /**
   * Initialize the SDK. Call this once when your game loads.
   * Returns the existing instance if init() was already called for the same gameKey.
   */
  static init(config: SubGamesConfig): SubGamesSDK {
    const existing = SubGamesSDK.instances.get(config.gameKey);
    if (existing) {
      console.warn(`[SubGames] SDK already initialized for gameKey "${config.gameKey}". Returning existing instance.`);
      return existing;
    }

    const sdk = new SubGamesSDK(config);
    SubGamesSDK.instances.set(config.gameKey, sdk);
    sdk.tryRestoreSession();
    sdk.listenForParentContext();
    sdk.startPromptTimer();
    // Always signal ready when in an iframe so the parent can send player context
    sdk.signalReady();
    // Try silent auth via hidden iframe for external domains
    sdk.trySilentAuth();
    if (config.overlay) {
      // Delay overlay init briefly to allow silent auth / parent context to respond.
      setTimeout(() => sdk.initOverlay(), 1500);
    }
    return sdk;
  }

  /**
   * Get the current player's tier relative to the game's creator.
   * Returns: 'none' | 'free' | 'supporter' | 'founder'
   */
  async getPlayerTier(): Promise<PlayerTier> {
    // If no token but we have a cached tier from the parent frame context, use it
    if (!this.token) return this.cachedTier || 'none';

    try {
      const res = await fetch(`${this.config.apiUrl}/game-access/tier/${this.config.gameKey}`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!res.ok) return 'none';
      const data = await res.json();
      this.cachedTier = data.tier;
      return data.tier;
    } catch {
      return 'none';
    }
  }

  /**
   * Check if the player meets a required tier.
   * If they don't, automatically shows the subscribe modal and pauses the game.
   * Returns true if they meet the tier, false if they don't (modal shown).
   */
  async requireTier(tier: PlayerTier, feature?: string): Promise<boolean> {
    const currentTier = this.cachedTier || (await this.getPlayerTier());
    if (TIER_RANK[currentTier] >= TIER_RANK[tier]) return true;

    // Player doesn't meet the tier — pause game and show subscribe prompt
    this.pause();
    await this.promptSubscribe(tier, feature);
    return false;
  }

  /**
   * Check if the player is logged in.
   */
  isLoggedIn(): boolean {
    return this.token !== null || this.platformAuthenticated;
  }

  /**
   * Get the current player's info.
   */
  async getPlayer(): Promise<PlayerInfo | null> {
    if (!this.token) return null;

    try {
      const res = await fetch(`${this.config.apiUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  }

  /**
   * Open a login prompt. Resolves when the user has logged in.
   * Pauses the game while the modal is open.
   */
  async promptLogin(): Promise<void> {
    this.pause();

    const inIframe = window.parent && window.parent !== window;

    // If embedded on sub.games, ask the parent to open the normal login modal
    if (inIframe) {
      try {
        window.parent.postMessage({ type: 'subgames:open-login' }, '*');
      } catch {}

      return new Promise((resolve) => {
        const handler = (event: MessageEvent) => {
          if (event.data?.type === 'subgames:player-context' && event.data.authenticated) {
            if (event.data.token) {
              this.token = event.data.token;
              localStorage.setItem('subgames_token', event.data.token);
            }
            this.cachedTier = event.data.tier || null;
            window.removeEventListener('message', handler);
            this.emit('login');
            this.unpause();
            resolve();
          }
        };
        window.addEventListener('message', handler);
      });
    }

    // Standalone: show inline iframe overlay with login page
    return new Promise((resolve) => {
      const existing = document.querySelector('.sg-login-backdrop');
      if (existing) existing.remove();

      const backdrop = document.createElement('div');
      backdrop.className = 'sg-login-backdrop';
      Object.assign(backdrop.style, {
        position: 'fixed',
        inset: '0',
        zIndex: '100002',
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      });

      const loginUrl = `${this.config.appUrl}/sdk/login?gameKey=${this.config.gameKey}&redirect=postMessage`;
      const iframe = document.createElement('iframe');
      iframe.src = loginUrl;
      Object.assign(iframe.style, {
        width: '420px',
        maxWidth: '95vw',
        height: '480px',
        maxHeight: '90vh',
        borderRadius: '16px',
        border: 'none',
        background: '#09090b',
      });

      backdrop.appendChild(iframe);

      const cleanup = () => {
        backdrop.remove();
        window.removeEventListener('message', handler);
        this.unpause();
        resolve();
      };

      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) cleanup();
      });

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'subgames:login' && event.data?.token) {
          this.token = event.data.token;
          localStorage.setItem('subgames_token', event.data.token);
          this.cachedTier = null;
          this.emit('login');
          cleanup();
        }
      };
      window.addEventListener('message', handler);

      document.body.appendChild(backdrop);
    });
  }

  /**
   * Open a subscribe prompt for the game's creator.
   * Pauses the game while the modal is open.
   * @param targetTier - The tier to suggest. Defaults to 'free'.
   */
  async promptSubscribe(targetTier: PlayerTier = 'free', feature?: string): Promise<void> {
    this.pause();

    // If embedded in a sub.games iframe, ask the parent to open the subscribe modal
    const inIframe = window.parent && window.parent !== window;
    if (inIframe) {
      try {
        window.parent.postMessage({ type: 'subgames:open-subscribe' }, '*');
      } catch {}

      return new Promise((resolve) => {
        const handler = (event: MessageEvent) => {
          if (event.data?.type === 'subgames:player-context') {
            // Parent sends updated context after subscription
            if (event.data.tier && event.data.tier !== 'none') {
              this.cachedTier = event.data.tier;
              if (event.data.token) {
                this.token = event.data.token;
                localStorage.setItem('subgames_token', event.data.token);
              }
              window.removeEventListener('message', handler);
              this.emit('tierChange', this.cachedTier || 'free');
              this.unpause();
              resolve();
            }
          }
        };
        window.addEventListener('message', handler);
      });
    }

    // Standalone: show inline iframe overlay with the subscribe modal page
    return new Promise(async (resolve) => {
      const existing = document.querySelector('.sg-subscribe-backdrop');
      if (existing) existing.remove();

      // Fetch creator slug from game key so we can load the real subscribe modal
      let subscribeUrl = `${this.config.appUrl}/sdk/subscribe?gameKey=${this.config.gameKey}&tier=${targetTier}`;
      if (feature) subscribeUrl += `&feature=${encodeURIComponent(feature)}`;
      try {
        const res = await fetch(`${this.config.apiUrl}/games/by-key/${this.config.gameKey}`);
        if (res.ok) {
          const game = await res.json();
          const slug = game?.creator?.slug;
          if (slug) subscribeUrl = `${this.config.appUrl}/@${slug}/sub`;
        }
      } catch {}

      const backdrop = document.createElement('div');
      backdrop.className = 'sg-subscribe-backdrop';
      Object.assign(backdrop.style, {
        position: 'fixed',
        inset: '0',
        zIndex: '100002',
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      });

      const iframe = document.createElement('iframe');
      iframe.src = subscribeUrl;
      Object.assign(iframe.style, {
        width: '420px',
        maxWidth: '95vw',
        height: '600px',
        maxHeight: '90vh',
        borderRadius: '16px',
        border: 'none',
        background: '#09090b',
      });

      backdrop.appendChild(iframe);

      const cleanup = () => {
        backdrop.remove();
        window.removeEventListener('message', handler);
        this.unpause();
        resolve();
      };

      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) cleanup();
      });

      const handler = (event: MessageEvent) => {
        if (event.data?.type === 'subgames:subscribed') {
          if (event.data.token) {
            this.token = event.data.token;
            localStorage.setItem('subgames_token', event.data.token);
          }
          this.cachedTier = event.data.tier || null;
          this.emit('tierChange', this.cachedTier || 'free');
          cleanup();
        }
        if (event.data?.type === 'subgames:subscribe-closed') {
          cleanup();
        }
      };
      window.addEventListener('message', handler);

      document.body.appendChild(backdrop);
    });
  }

  /**
   * Pause the game. Emits 'pause' event for the game to handle.
   */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.emit('pause');
  }

  /**
   * Unpause the game. Emits 'unpause' event for the game to handle.
   */
  unpause(): void {
    if (!this.paused) return;
    this.paused = false;
    this.emit('unpause');
  }

  /**
   * Whether the game is currently paused by the SDK.
   */
  isPaused(): boolean {
    return this.paused;
  }

  /**
   * Get the current auth token.
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Set the auth token (used when login happens externally).
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('subgames_token', token);
    this.cachedTier = null;
    this.emit('login');
  }

  /**
   * Log out the current player.
   */
  logout(): void {
    this.token = null;
    this.cachedTier = null;
    localStorage.removeItem('subgames_token');
    this.emit('logout');
  }

  /**
   * Listen for SDK events.
   */
  on<K extends keyof SubGamesEventMap>(event: K, handler: SubGamesEventMap[K]): void {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event]!.push(handler);
  }

  /**
   * Remove an event listener.
   */
  off<K extends keyof SubGamesEventMap>(event: K, handler: SubGamesEventMap[K]): void {
    const list = this.listeners[event];
    if (!list) return;
    const idx = list.indexOf(handler);
    if (idx !== -1) list.splice(idx, 1);
  }

  private emit<K extends keyof SubGamesEventMap>(
    event: K,
    ...args: Parameters<SubGamesEventMap[K]>
  ): void {
    const list = this.listeners[event];
    if (!list) return;
    for (const handler of list) {
      (handler as Function)(...args);
    }
  }

  /**
   * Signal to the parent page (sub.games platform) that the SDK is active.
   * Retries every 200ms until the parent acknowledges with sdk-ready-ack.
   */
  private signalReady(): void {
    const send = () => {
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'subgames:sdk-ready' }, '*');
        }
      } catch {
        // Cross-origin — can't message parent
      }
    };

    const onAck = (event: MessageEvent) => {
      if (event.data?.type === 'subgames:sdk-ready-ack') {
        if (this.readyInterval) clearInterval(this.readyInterval);
        window.removeEventListener('message', onAck);
      }
    };
    window.addEventListener('message', onAck);

    send();
    this.readyInterval = setInterval(send, 200);
    // Stop retrying after 5s regardless
    setTimeout(() => {
      if (this.readyInterval) clearInterval(this.readyInterval);
      window.removeEventListener('message', onAck);
    }, 5000);
  }

  private isEmbeddedInPlatform(): boolean {
    try {
      if (window.parent === window) return false;
      // Check if parent is the sub.games platform via referrer or appUrl match
      const parentOrigin = document.referrer ? new URL(document.referrer).origin : '';
      return parentOrigin === this.config.appUrl;
    } catch {
      // Cross-origin — can't access parent, but check referrer
      const parentOrigin = document.referrer ? new URL(document.referrer).origin : '';
      return parentOrigin === this.config.appUrl;
    }
  }

  /**
   * Listen for player context from the parent frame (sub.games game page).
   * When embedded in an iframe, the parent sends auth state and tier info
   * so the SDK can recognize the logged-in user without a separate token.
   */
  private listenForParentContext(): void {
    try {
      if (window.parent === window) return; // Not in an iframe
    } catch {
      // Cross-origin — still listen
    }

    window.addEventListener('message', (event: MessageEvent) => {
      if (event.data?.type === 'subgames:player-context') {
        const { authenticated, tier } = event.data;
        if (authenticated) {
          this.platformAuthenticated = true;
          if (tier) this.cachedTier = tier as PlayerTier;
          this.emit('login');
          if (tier) this.emit('tierChange', this.cachedTier!);
        }
      }
    });
  }

  private tryRestoreSession(): void {
    try {
      const token = localStorage.getItem('subgames_token');
      if (token) this.token = token;
    } catch {
      // localStorage not available (SSR, privacy mode, etc.)
    }
  }

  /**
   * Attempt silent auth by loading a hidden iframe on the app domain.
   * The iframe checks the user's session cookie (same-origin) and posts
   * back auth state via postMessage. This works on external domains where
   * the SDK can't access sub.games cookies directly.
   */
  private trySilentAuth(): void {
    // Skip if already authenticated via token or parent context
    if (this.token || this.platformAuthenticated) return;
    // Skip if embedded in platform (parent context handles auth)
    if (this.isEmbeddedInPlatform()) return;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${this.config.appUrl}/sdk/auth-check?gameKey=${encodeURIComponent(this.config.gameKey)}&origin=${encodeURIComponent(window.location.origin)}`;
    document.body.appendChild(iframe);

    const handler = (event: MessageEvent) => {
      // Verify the message comes from our app domain
      const expectedOrigin = new URL(this.config.appUrl).origin;
      if (event.origin !== expectedOrigin) return;
      if (event.data?.type !== 'subgames:auth-check') return;

      window.removeEventListener('message', handler);
      iframe.remove();

      if (event.data.authenticated) {
        this.platformAuthenticated = true;
        if (event.data.tier) this.cachedTier = event.data.tier as PlayerTier;
        this.emit('login');
        if (event.data.tier && event.data.tier !== 'none') {
          this.emit('tierChange', this.cachedTier!);
        }
      }
    };

    window.addEventListener('message', handler);

    // Clean up after 5s if no response (iframe blocked, no session, etc.)
    setTimeout(() => {
      window.removeEventListener('message', handler);
      if (iframe.parentNode) iframe.remove();
    }, 5000);
  }

  /**
   * Start the mandatory 30s subscribe prompt. Not configurable by developers.
   * Developers can add additional prompts via requireTier() for gated features.
   */
  private startPromptTimer(): void {
    this.promptTimer = setTimeout(async () => {
      const tier = await this.getPlayerTier();
      if (tier === 'none') {
        const promptTier: PlayerTier = 'free';
        await this.promptSubscribe(promptTier);
      }
    }, 30_000);
  }

  private async initOverlay(): Promise<void> {
    const { createOverlay } = await import('./overlay');
    createOverlay(this, this.config);
  }
}
